import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import {
  isModuleEnabled,
  TENANT_MODULE_KEYS,
  type TenantModuleKey,
  type TenantModules,
  type TenantModuleTemplate,
} from '@dosfilos/domain';
import { db } from '../../lib/firebase';
import { errorText } from '../../lib/error-text';
import { tenantModulesApi } from '../../lib/api/tenant-modules.api';
import { useConfirm } from '../ConfirmProvider';

// ─── Módulos del seminario ──────────────────────────────────────────────────
// En Configuración del seminario (su administración) y en la consola de
// plataforma, que además aplica una plantilla («SIS» o «Completo»). Cada
// interruptor guarda al momento y queda en la auditoría del seminario.

/** Los módulos guardados de un seminario. La clave la comparte el menú
 *  (`useTenantConfig`), que se invalida al guardar. */
export function useTenantModules(tenantId: string | null | undefined) {
  return useQuery({
    queryKey: ['tenant-modules', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<TenantModules> => {
      const snap = await getDoc(doc(db, `tenants/${tenantId}`));
      return ((snap.data()?.['config'] as { modules?: TenantModules } | undefined)?.modules ??
        {}) as TenantModules;
    },
  });
}

export function TenantModulesPanel({
  tenantId,
  platform = false,
}: {
  tenantId: string;
  /** La consola de plataforma: muestra también las plantillas. */
  platform?: boolean;
}): JSX.Element {
  const { t } = useTranslation('common');
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const modulesQ = useTenantModules(tenantId);
  const [saving, setSaving] = useState<TenantModuleKey | TenantModuleTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save(
    key: TenantModuleKey | TenantModuleTemplate,
    input: { modules?: TenantModules; template?: TenantModuleTemplate },
  ) {
    setSaving(key);
    setError(null);
    try {
      await tenantModulesApi.set({ tenantId, ...input });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tenant-modules', tenantId] }),
        queryClient.invalidateQueries({ queryKey: ['tenant-config-view', tenantId] }),
      ]);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(null);
    }
  }

  async function applyTemplate(template: TenantModuleTemplate) {
    const ok = await confirm({
      title: t(`modules.templates.${template}.title`),
      message: t(`modules.templates.${template}.confirm`),
      confirmLabel: t('modules.templates.apply'),
    });
    if (ok) await save(template, { template });
  }

  if (modulesQ.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  const modules = modulesQ.data ?? {};

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700}>
          {t('modules.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('modules.intro')}
        </Typography>
      </Box>
      <Alert severity="info">{t('modules.scheduledNote')}</Alert>

      {platform && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {(['sis', 'full'] as const).map((template) => (
            <Button
              key={template}
              variant="outlined"
              size="small"
              disabled={saving !== null}
              onClick={() => void applyTemplate(template)}
            >
              {t(`modules.templates.${template}.title`)}
            </Button>
          ))}
        </Stack>
      )}

      <Stack spacing={1.5}>
        {TENANT_MODULE_KEYS.map((key) => (
          <FormControlLabel
            key={key}
            sx={{ alignItems: 'flex-start', m: 0 }}
            control={
              <Switch
                size="small"
                checked={isModuleEnabled(modules, key)}
                disabled={saving !== null}
                onChange={(e) => void save(key, { modules: { [key]: e.target.checked } })}
                inputProps={{ 'aria-label': t(`modules.keys.${key}.label`) }}
              />
            }
            label={
              <Box sx={{ ml: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {t(`modules.keys.${key}.label`)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(`modules.keys.${key}.hides`)}
                </Typography>
              </Box>
            }
          />
        ))}
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}
